import {
  Dimensions,
  getViewportForBounds,
  Rect,
  Viewport,
} from '@xyflow/react';

/**
 * Room along one axis. The container measurement spans chrome outside the canvas
 * (the diagram card's legend band), so it over-reports; the canvas can also
 * outrun its container where a min-height pins it taller and the ancestor
 * scrolls. Take the smaller; a 0 means unmeasured and defers to the other.
 */
function getAvailableExtent(canvas: number, container: number): number {
  if (!canvas) return container;
  if (!container) return canvas;
  return Math.min(canvas, container);
}

export interface FitViewportInput {
  /**
   * Bounds driving horizontal placement and the width the zoom must satisfy.
   * Excludes section labels: they're stretched to the viewport width, which
   * would drag zoom below what the content needs.
   */
  boundsForZoom: Rect;
  /** Bounds of everything visible — the vertical anchor. */
  boundsY: Rect;
  /** React Flow's measured canvas — the surface the transform applies to. */
  canvas: Dimensions;
  /** The measured container the canvas sits in. */
  container: Dimensions;
  /** Space held back above the content. */
  topBuffer: number;
  /** Fraction of the fit box left empty around the content. */
  paddingRatio: number;
  minZoom: number;
  maxZoom: number;
  /**
   * Fit to the canvas with the top buffer reserved, and size the height against
   * `boundsY`. Only the entity diagram & notes slide opts in (T1-6663); the
   * other surfaces keep the legacy container fit so their layout doesn't shift.
   */
  fitToCanvas: boolean;
}

/**
 * Viewport that fits the diagram: `boundsForZoom` centered horizontally,
 * `boundsY` top-aligned {@link FitViewportInput.topBuffer} below the top.
 */
export function getFitViewport({
  boundsForZoom,
  boundsY,
  canvas,
  container,
  topBuffer,
  paddingRatio,
  minZoom,
  maxZoom,
  fitToCanvas,
}: FitViewportInput): Viewport {
  // Top-align rather than vertically center, so the top section label stays put
  // instead of drifting with the content height.
  const getViewport = ({ x, zoom }: Viewport): Viewport => ({
    x,
    y: -1 * boundsY.y * zoom + topBuffer,
    zoom,
  });

  if (!fitToCanvas) {
    // Legacy fit: the raw container height, with the buffer applied afterwards.
    // Over-reports the room available, so tall content overflows the bottom.
    return getViewport(
      getViewportForBounds(
        boundsForZoom,
        container.width,
        container.height,
        minZoom,
        maxZoom,
        paddingRatio
      )
    );
  }

  // Width/x from the content bounds, height/y from the full bounds. The buffer
  // comes off the available height *before* the fit, because the content is
  // then shifted down by it.
  const boundsToFit: Rect = {
    x: boundsForZoom.x,
    width: boundsForZoom.width,
    y: boundsY.y,
    height: boundsY.height,
  };

  return getViewport(
    getViewportForBounds(
      boundsToFit,
      getAvailableExtent(canvas.width, container.width),
      Math.max(
        getAvailableExtent(canvas.height, container.height) - topBuffer,
        0
      ),
      minZoom,
      maxZoom,
      paddingRatio
    )
  );
}
